import { useCallback, useEffect, useState } from "react";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import { prototypeSignatureService } from "./prototypeSignatureService";
import type {
  PrototypeSignatureProvider,
  PrototypeSignatureSession,
} from "./types";

export interface UsePrototypeSignatureSessionResult {
  session: PrototypeSignatureSession | null;
  isSessionActive: boolean;
  isStarting: boolean;
  isTerminating: boolean;
  errorMessage?: string;
  startSession: (provider: PrototypeSignatureProvider) => Promise<void>;
  endSession: () => Promise<void>;
}

/**
 * usePrototypeSignatureSession
 *
 * Hook de domínio/protótipo para gerenciar o fluxo de sessão de assinatura digital.
 *
 * Responsabilidades:
 * - Carregar a sessão atual armazenada (quando existir).
 * - Orquestrar o início e fim de sessão via `prototypeSignatureService`.
 * - Expor flags simples de loading/erro para a UI (sidebar/dialog).
 *
 * Ponto de integração futura:
 * - Substituir `prototypeSignatureService` por chamadas reais ao backend.
 */
export function usePrototypeSignatureSession(): UsePrototypeSignatureSessionResult {
  const [session, setSession] = useState<PrototypeSignatureSession | null>(
    null,
  );

  const {
    run: runStartSession,
    loading: isStarting,
    error: startError,
  } = useAsyncTask<[PrototypeSignatureProvider], PrototypeSignatureSession, string>(
    {
      task: async (provider) => {
        const newSession = await prototypeSignatureService.startSession(provider);
        setSession(newSession);
        return newSession;
      },
      mapError: () => "Não foi possível iniciar a sessão de assinatura.",
    },
  );

  const {
    run: runEndSession,
    loading: isTerminating,
    error: endError,
  } = useAsyncTask<[], void, string>({
    task: async () => {
      await prototypeSignatureService.endSession();
      setSession(null);
    },
    mapError: () => "Não foi possível encerrar a sessão de assinatura.",
  });

  const loadInitialSession = useCallback(async () => {
    try {
      const currentSession =
        await prototypeSignatureService.getCurrentSession();
      setSession(currentSession);
    } catch {
      // Silencia erros no carregamento inicial (protótipo).
    }
  }, []);

  useEffect(() => {
    void loadInitialSession();
  }, [loadInitialSession]);

  const startSession = useCallback(
    async (provider: PrototypeSignatureProvider): Promise<void> => {
      await runStartSession(provider);
    },
    [runStartSession],
  );

  const endSession = useCallback(async (): Promise<void> => {
    await runEndSession();
  }, [runEndSession]);

  return {
    session,
    isSessionActive: Boolean(session),
    isStarting,
    isTerminating,
    errorMessage: (startError ?? endError) as string | undefined,
    startSession,
    endSession,
  };
}

